import React from 'react';
import { StructuredData } from '../..';
import {
    FACEBOOK_URL_START,
    YELP_URL_START,
    MILAN_PHONE,
    FIREBASE_STORAGE_PATH,
    storeHours,
    buildDailyHoursMap,
    to24HourTime,
} from '../../../utils';
const TXRoom = `${FIREBASE_STORAGE_PATH}general%2Fseo%2FTXRoom.webp?alt=media&token=f59ef1d6-65ce-429e-8f41-a688657ef2d9`;
const MILAN_LOGO = `${FIREBASE_STORAGE_PATH}general%2Fseo%2FMilan-Logo-Blue.webp?alt=media&token=eacafc73-400d-4131-bde8-3b469aca0229`;

const schemaOrgDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function toSchemaOrgOpeningHours(entries) {
    const dailyMap = buildDailyHoursMap(entries);
    const result = [];

    let i = 0;
    while (i < 7) {
        const schedule = dailyMap[i];
        if (!schedule) {
            i++;
            continue;
        }
        const hoursKey = `${schedule.open}|${schedule.close}`;
        let j = i + 1;
        while (j < 7 && dailyMap[j] && `${dailyMap[j].open}|${dailyMap[j].close}` === hoursKey) {
            j++;
        }
        const [openH, openM] = to24HourTime(schedule.open);
        const [closeH, closeM] = to24HourTime(schedule.close);
        const openStr = `${String(openH).padStart(2, '0')}:${String(openM).padStart(2, '0')}`;
        const closeStr = `${String(closeH).padStart(2, '0')}:${String(closeM).padStart(2, '0')}`;
        const dayRange = j - i > 1 ? `${schemaOrgDays[i]}-${schemaOrgDays[j - 1]}` : schemaOrgDays[i];
        result.push(`${dayRange} ${openStr}-${closeStr}`);
        i = j;
    }

    return result;
}

// storeHours is a module-level constant — compute the Schema.org representation once at import time.
const OPENING_HOURS = toSchemaOrgOpeningHours(storeHours);

export function StoreStructuredData({ currentMetro, currentStore }) {
    const storeUrl =
        currentMetro.stores.length > 1
            ? `${currentMetro.origin}/locations/${currentMetro.metroPath}/${currentStore.pathname}/`
            : `${currentMetro.origin}/locations/${currentMetro.metroPath}/`;

    const storeName = `Milan Laser Hair Removal - ${currentStore.marketingCity}`;
    const storePhone = currentStore.phone === '' ? MILAN_PHONE : currentStore.phone;

    const breadcrumbItems = [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${currentMetro.origin}` },
        { '@type': 'ListItem', position: 2, name: 'Locations', item: `${currentMetro.origin}/locations/` },
        {
            '@type': 'ListItem',
            position: 3,
            name: currentMetro.name,
            item: `${currentMetro.origin}/locations/${currentMetro.metroPath}/`,
        },
    ];

    if (currentMetro.stores.length > 1) {
        breadcrumbItems.push({ '@type': 'ListItem', position: 4, name: currentStore.marketingCity });
    }

    return (
        <>
            <StructuredData>
                {{
                    '@context': 'https://schema.org',
                    '@type': 'MedicalBusiness',
                    name: storeName,
                    url: storeUrl,
                    sameAs: [
                        `${FACEBOOK_URL_START}${currentStore.platforms.facebook}`,
                        `${YELP_URL_START}${currentStore.platforms.yelp}`,
                    ],
                    logo: MILAN_LOGO,
                    image: TXRoom,
                    priceRange: '$$',
                    telephone: storePhone,
                    description:
                        "Laser Hair Removal | Milan Laser is one of the nation's leading laser hair removal companies. We are committed to providing our clients with world-class customer service and permanent results. Our physicians and highly-trained medical staff utilize ongoing medical research to ensure that all treatments are performed safely and are tailored to give every client the best results possible. Our goal is to provide 100% customer satisfaction to every client and we will do everything we can to ensure our clients are happy with their experience as well as their results.",
                    address: {
                        '@type': 'PostalAddress',
                        streetAddress: currentStore.address,
                        addressLocality:
                            currentStore.addressCity === '' ? currentStore.marketingCity : currentStore.addressCity,
                        addressRegion: currentStore.stateAbbrev,
                        postalCode: currentStore.zipCode,
                        addressCountry: 'US',
                    },
                    geo: {
                        '@type': 'GeoCoordinates',
                        latitude: currentStore.geo.lat,
                        longitude: currentStore.geo.long,
                    },
                    hasMap: currentStore.maps.google,
                    openingHours: currentStore.open ? OPENING_HOURS : undefined,
                    medicalSpecialty: 'Dermatology',
                    currenciesAccepted: 'USD',
                    paymentAccepted: 'Cash, Credit Card, Financing',
                    contactPoint: {
                        '@type': 'ContactPoint',
                        telephone: storePhone,
                        contactType: 'customer service',
                    },
                    aggregateRating:
                        currentStore.rating && currentStore.reviewCount
                            ? {
                                  '@type': 'AggregateRating',
                                  ratingValue: currentStore.rating.toString(),
                                  reviewCount: currentStore.reviewCount.toString(),
                                  bestRating: '5',
                              }
                            : undefined,
                }}
            </StructuredData>

            <StructuredData>
                {{
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: breadcrumbItems,
                }}
            </StructuredData>

            <StructuredData>
                {{
                    '@context': 'https://schema.org',
                    '@type': 'WebPage',
                    name: `Laser Hair Removal in ${currentStore.marketingCity}, ${currentStore.stateAbbrev} - Milan Laser`,
                    description: `Visit Milan Laser in ${currentStore.marketingCity}, ${currentStore.stateAbbrev} for permanent laser hair removal. Located at ${currentStore.address}.`,
                    url: storeUrl,
                }}
            </StructuredData>

            <StructuredData>
                {{
                    '@context': 'https://schema.org',
                    '@type': 'Service',
                    serviceType: 'Laser Hair Removal',
                    name: `Laser Hair Removal in ${currentStore.marketingCity}, ${currentStore.stateAbbrev}`,
                    description: `Permanent laser hair removal for all skin tones, performed by medical professionals at our ${currentStore.marketingCity} clinic.`,
                    provider: {
                        '@type': 'MedicalBusiness',
                        name: storeName,
                        url: storeUrl,
                    },
                    areaServed: {
                        '@type': 'City',
                        name: `${currentStore.marketingCity}, ${currentStore.stateAbbrev}`,
                    },
                    url: storeUrl,
                }}
            </StructuredData>
            {/*
             * 2e. FAQPage — commented out until FAQ content is added to each store page.
             * Once FAQ sections are live on clinic pages, replace the placeholder questions
             * below with store-specific content and uncomment this block.
             *
             * <StructuredData>
             *   {{
             *     '@context': 'https://schema.org',
             *     '@type': 'FAQPage',
             *     mainEntity: [
             *       {
             *         '@type': 'Question',
             *         name: `What is the best laser hair removal clinic in ${currentStore.marketingCity}?`,
             *         acceptedAnswer: {
             *           '@type': 'Answer',
             *           text: `Milan Laser in ${currentStore.marketingCity} offers safe, effective laser hair removal for all skin tones, overseen by medical professionals.`,
             *         },
             *       },
             *       {
             *         '@type': 'Question',
             *         name: `How much does laser hair removal cost in ${currentStore.marketingCity}?`,
             *         acceptedAnswer: {
             *           '@type': 'Answer',
             *           text: 'Cost varies by body area treated and any active specials. Financing options are available with 0% APR.',
             *         },
             *       },
             *       // ... add additional FAQ items
             *     ],
             *   }}
             * </StructuredData>
             */}

            {/*
             * 2f. Person — commented out until named staff are published on each store page.
             * Once staff names and titles are live, add one Person entry per staff member.
             *
             * <StructuredData>
             *   {{
             *     '@context': 'https://schema.org',
             *     '@type': 'Person',
             *     name: '[Staff Member Name]',
             *     jobTitle: '[Staff Member Title]',
             *     worksFor: {
             *       '@type': 'MedicalBusiness',
             *       name: storeName,
             *       url: storeUrl,
             *     },
             *   }}
             * </StructuredData>
             */}
        </>
    );
}
